import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';

function Profile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    tax_number: '',
    phone: '',
    address: '',
  });
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/profile');
      setUser(response.data.user);
      setFormData({
        business_name: response.data.user.business_name || '',
        tax_number: response.data.user.tax_number || '',
        phone: response.data.user.phone || '',
        address: response.data.user.address || '',
      });
    } catch (error) {
      console.error('Profil yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await api.put('/profile', formData);
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setMessage('✅ Profil başarıyla güncellendi!');
      setEditing(false);
    } catch (error) {
      setMessage('❌ ' + (error.response?.data?.error || 'Bir hata oluştu!'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && !user) {
    return (
      <Layout activePage="profile">
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-500">Yükleniyor...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout activePage="profile">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row items-center gap-4 md:justify-between">
            <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
              <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center text-white text-3xl font-bold">
                {user?.business_name?.charAt(0) || 'U'}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{user?.business_name}</h1>
                <p className="text-gray-500">{user?.email}</p>
              </div>
            </div>
            
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                ✏️ Düzenle
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {editing ? (
          // Edit Form
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Profili Düzenle</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  İşletme Adı
                </label>
                <input
                  type="text"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vergi/TC Kimlik No
                </label>
                <input
                  type="text"
                  name="tax_number"
                  value={formData.tax_number}
                  onChange={handleChange}
                  required
                  maxLength="11"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefon
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adres
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>

              {message && (
                <div className={`p-3 rounded-lg text-sm ${
                  message.includes('✅') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {message}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {loading ? 'Kaydediliyor...' : '💾 Kaydet'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setMessage('');
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        ) : (
          // View Mode
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Profil Bilgileri</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">İşletme Adı</label>
                <p className="text-gray-800">{user?.business_name}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                <p className="text-gray-800">{user?.email}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Vergi/TC Kimlik No</label>
                <p className="text-gray-800">{user?.tax_number}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Telefon</label>
                <p className="text-gray-800">{user?.phone}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Adres</label>
                <p className="text-gray-800">{user?.address}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Kayıt Tarihi</label>
                <p className="text-gray-800">
                  {new Date(user?.created_at).toLocaleDateString('tr-TR')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Profile;